import './doctor.scss';
import React, { useMemo, useEffect, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import { toJstDateInputValue } from '../../utils/dateTimeJst';
import AlertModal from '../common/AlertModal';
import DoctorPageHeader from './DoctorPageHeader';

const pad2 = (n) => String(n).padStart(2, '0');
const HOUR_OPTS = Array.from({ length: 16 }, (_, i) => i + 8); // 8〜23（開始時間用）
const HOUR_OPTS_END = Array.from({ length: 17 }, (_, i) => i + 8); // 8〜24（終了時間用）
const MIN_OPTS  = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10, ... 55

const ShiftCalenderContainer = () => {
  const [tab, setTab] = useState('shift'); // 'shift' | 'duration'
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  
  // 週次データ
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // シフト作成モーダル
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [startH, setStartH] = useState(9);
  const [startM, setStartM] = useState(0);
  const [endH, setEndH] = useState(18);
  const [endM, setEndM] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // 選択されたスロット（削除用）
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);

  // 時間枠設定（従来通り）
  const [defDur, setDefDur] = useState({ first_visit: 20, follow_up: 15 });
  const [specRows, setSpecRows] = useState([]);
  
  // 疾患別設定（新規）
  const [diseaseRows, setDiseaseRows] = useState([]);
  const [availableDiseases, setAvailableDiseases] = useState([]);
  const [showDiseaseModal, setShowDiseaseModal] = useState(false);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState('');

  // AlertModal用のstate
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    type: 'alert',
    iconType: null,
    onConfirm: null
  });

  // AlertModalを閉じる
  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, open: false }));
  };

  // アラートを表示
  const showAlert = (title, message, iconType = null) => {
    setAlertModal({
      open: true,
      title,
      message,
      type: 'alert',
      iconType,
      onConfirm: null
    });
  };

  // 確認ダイアログを表示
  const showConfirm = (title, message, onConfirm) => {
    setAlertModal({
      open: true,
      title,
      message,
      type: 'confirm',
      iconType: 'confirm',
      onConfirm
    });
  };

  const totalMinutes = useMemo(() => {
    const start = startH * 60 + startM;
    const end   = endH   * 60 + endM;
    return Math.max(0, end - start);
  }, [startH, startM, endH, endM]);
  
  const durationText = useMemo(() => {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m === 0 ? `${h}時間` : `${h}時間${m}分`;
  }, [totalMinutes]);

  // 週次データ取得
  const fetchWeek = async (date) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/api/medichat_doctor/shifts/week', {
        params: { date: date || toJstDateInputValue() }
      });
      setWeekData(data);
      setCurrentWeekStart(data.week_start);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'shift') {
      fetchWeek();
    }
  }, [tab]);

  const goPrevWeek = () => {
    if (!currentWeekStart) return;
    const prevWeek = new Date(currentWeekStart);
    prevWeek.setDate(prevWeek.getDate() - 7);
    fetchWeek(toJstDateInputValue(prevWeek));
  };

  const goNextWeek = () => {
    if (!currentWeekStart) return;
    const nextWeek = new Date(currentWeekStart);
    nextWeek.setDate(nextWeek.getDate() + 7);
    if (weekData?.entry_max_date && toJstDateInputValue(nextWeek) > weekData.entry_max_date) return;
    fetchWeek(toJstDateInputValue(nextWeek));
  };

  const canGoNextWeek = useMemo(() => {
    if (!currentWeekStart || !weekData?.entry_max_date) return true;
    const nextWeek = new Date(currentWeekStart);
    nextWeek.setDate(nextWeek.getDate() + 7);
    return toJstDateInputValue(nextWeek) <= weekData.entry_max_date;
  }, [currentWeekStart, weekData?.entry_max_date]);

  // スロット選択トグル
  const toggleSlot = (dayIndex, slotIndex) => {
    if (!weekData) return;
    const day = weekData.days[dayIndex];
    const slot = day.slots[slotIndex];
    
    // 予約済みは選択不可
    if (slot.state === 'booked') return;
    // 空のスロットは選択不可
    if (slot.state === 'empty') return;
    
    const slotIdsToToggle = slot.slot_ids;
    
    setSelectedSlotIds(prev => {
      const newSet = new Set(prev);
      const allSelected = slotIdsToToggle.every(id => newSet.has(id));
      
      if (allSelected) {
        // すべて選択されている → すべて解除
        slotIdsToToggle.forEach(id => newSet.delete(id));
      } else {
        // 一部または未選択 → すべて選択
        slotIdsToToggle.forEach(id => newSet.add(id));
      }
      
      return Array.from(newSet);
    });
  };

  // シフト作成モーダルを開く
  const openCreateModal = () => {
    // デフォルトは今日の日付
    const today = toJstDateInputValue();
    setSelectedDate(today);
    setStartH(9);
    setStartM(0);
    setEndH(18);
    setEndM(0);
    setIsCreateModalOpen(true);
  };

  // シフト作成
  const createShift = async () => {
    if (!selectedDate) return;
    if (weekData?.entry_max_date && selectedDate > weekData.entry_max_date) {
      showAlert('入力できません', `シフトは${weekData.entry_max_date}まで入力できます。`);
      return;
    }
    try {
      const { data } = await axiosInstance.post('/api/medichat_doctor/shifts/create_range', {
        date: selectedDate,
        start_hour: startH,
        start_minute: startM,
        end_hour: endH,
        end_minute: endM
      });

      const createdCount = Number(data?.created || 0);
      const isLimited = !!data?.limited;
      const serverMessage = data?.message;

      if (isLimited && createdCount === 0) {
        showAlert('作成できませんでした', serverMessage || '指定時間帯はシフト上限に達しているため、作成されませんでした。');
      } else if (isLimited) {
        showAlert('一部作成', serverMessage || `${createdCount}件作成しました（一部時間帯は上限のため未作成です）。`);
      } else if (createdCount === 0) {
        showAlert('作成対象なし', '指定時間帯に新規作成できるシフトがありませんでした。');
      } else {
        showAlert('成功', `${createdCount}件のシフトを作成しました`);
      }

      setIsCreateModalOpen(false);
      fetchWeek(currentWeekStart);
    } catch (error) {
      console.error('シフト作成エラー:', error);
      const errorMessage = error.response?.data?.message || 'シフトの作成に失敗しました';
      showAlert('エラー', errorMessage);
    }
  };

  // 選択したシフトを削除
  const deleteSelected = async () => {
    if (selectedSlotIds.length === 0) return;
    
    showConfirm(
      '確認',
      `${selectedSlotIds.length}件のシフトを削除しますか？`,
      async () => {
        try {
          await axiosInstance.delete('/api/medichat_doctor/shifts/destroy_many', {
            data: { ids: selectedSlotIds }
          });
          showAlert('成功', 'シフトを削除しました');
          setSelectedSlotIds([]);
          fetchWeek(currentWeekStart);
        } catch (error) {
          console.error('シフト削除エラー:', error);
          showAlert('エラー', 'シフトの削除に失敗しました');
        }
      }
    );
  };

  // スロットの表示記号
  const getSlotDisplay = (state) => {
    switch (state) {
      case 'booked': return '予';
      case 'full': return '○';
      case 'half': return '△';
      case 'empty': return '×'; // 細いバツ記号に変更（視認性向上）
      default: return '-';
    }
  };

  // スロットが選択されているか
  const isSlotSelected = (slot) => {
    if (!slot.slot_ids || slot.slot_ids.length === 0) return false;
    return slot.slot_ids.some(id => selectedSlotIds.includes(id));
  };

  // 時間枠設定のロード・保存（従来通り）
  // 初診は最小10分、再診は最小5分
  const normalizeFirstVisit = (n) => {
    const v = Math.max(10, Math.min(240, Number(n) || 0));
    return Math.round(v / 5) * 5;
  };
  const normalizeFollowUp = (n) => {
    const v = Math.max(5, Math.min(240, Number(n) || 0));
    return Math.round(v / 5) * 5;
  };
  const normalize = (key, val) => {
    return key === 'first_visit' ? normalizeFirstVisit(val) : normalizeFollowUp(val);
  };

  const loadDurations = async () => {
    const { data } = await axiosInstance.get('/api/medichat_doctor/duration_settings');
    setDefDur({
      first_visit: data?.default?.first_visit ?? 20,
      follow_up  : data?.default?.follow_up   ?? 15,
    });
    setSpecRows((data?.specialties || []).map(r => ({
      specialty_id  : r.specialty_id,
      specialty_name: r.specialty_name,
      first_visit   : r.first_visit  ?? (data?.default?.first_visit ?? 20),
      follow_up     : r.follow_up    ?? (data?.default?.follow_up   ?? 15),
    })));
    // 疾患別設定
    setDiseaseRows((data?.diseases || []).map(r => ({
      disease_id: r.disease_id,
      disease_code: r.disease_code,
      disease_name: r.disease_name,
      specialty_name: r.specialty_name,
      first_visit: r.first_visit ?? '',
      follow_up: r.follow_up ?? '',
    })));
  };

  const loadAvailableDiseases = async () => {
    const { data } = await axiosInstance.get('/api/medichat_doctor/duration_settings/available_diseases');
    setAvailableDiseases(data?.diseases || []);
  };

  useEffect(() => {
    if (tab === 'duration') {
      loadDurations();
      loadAvailableDiseases();
    }
  }, [tab]);

  const onChangeDefault = (key, val) => {
    setDefDur(prev => ({ ...prev, [key]: val }));
  };
  const onBlurDefault = (key, val) => {
    setDefDur(prev => ({ ...prev, [key]: normalize(key, val) }));
  };

  const onChangeRow = (idx, key, val) => {
    setSpecRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });
  };
  const onBlurRow = (idx, key, val) => {
    setSpecRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: normalize(key, val) };
      return next;
    });
  };

  // 疾患別設定の変更ハンドラ
  const onChangeDiseaseRow = (idx, key, val) => {
    setDiseaseRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });
  };
  const onBlurDiseaseRow = (idx, key, val) => {
    if (val === '' || val === null || val === undefined) return;
    setDiseaseRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: normalize(key, val) };
      return next;
    });
  };

  // 疾患を追加
  const addDisease = () => {
    if (!selectedDiseaseId) return;
    const disease = availableDiseases.find(d => d.id === Number(selectedDiseaseId));
    if (!disease) return;
    // 既に追加済みかチェック
    if (diseaseRows.some(r => r.disease_id === disease.id)) {
      showAlert('エラー', 'この疾患は既に追加されています');
      return;
    }
    setDiseaseRows(prev => [...prev, {
      disease_id: disease.id,
      disease_code: disease.disease_code,
      disease_name: disease.disease_name,
      specialty_name: disease.specialty_name,
      first_visit: '',
      follow_up: '',
    }]);
    setSelectedDiseaseId('');
    setShowDiseaseModal(false);
  };

  // 疾患を削除
  const removeDisease = (idx) => {
    setDiseaseRows(prev => prev.filter((_, i) => i !== idx));
  };

  const saveAllDurations = async () => {
    // バリデーション: 初診は10分以上、再診は5分以上
    const errors = [];
    
    if (Number(defDur.first_visit) < 10) {
      errors.push('デフォルトの初診時間は10分以上を設定してください');
    }
    if (Number(defDur.follow_up) < 5) {
      errors.push('デフォルトの再診時間は5分以上を設定してください');
    }
    
    specRows.forEach(r => {
      if (Number(r.first_visit) < 10) {
        errors.push(`「${r.specialty_name}」の初診時間は10分以上を設定してください`);
      }
      if (Number(r.follow_up) < 5) {
        errors.push(`「${r.specialty_name}」の再診時間は5分以上を設定してください`);
      }
    });
    
    if (errors.length > 0) {
      showAlert('入力エラー', errors.join('\n'));
      return;
    }
    
    const payload = {
      default: {
        first_visit: normalizeFirstVisit(defDur.first_visit),
        follow_up  : normalizeFollowUp(defDur.follow_up),
      },
      specialties: specRows.map(r => ({
        specialty_id: r.specialty_id,
        first_visit : normalizeFirstVisit(r.first_visit),
        follow_up   : normalizeFollowUp(r.follow_up),
      })),
      diseases: diseaseRows.map(r => ({
        disease_id: r.disease_id,
        first_visit: r.first_visit ? normalizeFirstVisit(r.first_visit) : null,
        follow_up: r.follow_up ? normalizeFollowUp(r.follow_up) : null,
      })),
    };
    await axiosInstance.post('/api/medichat_doctor/duration_settings/bulk_save', payload);
    showAlert('成功', '時間枠を保存しました');
  };

  return (
    <div className="doctor-page-shell">
      <div className="shiftdoc-container">
      <DoctorPageHeader
        title="シフト管理"
      />
      {/* 上部タブ */}
      <div className="shiftdoc-tabs">
        <button 
          className={`shiftdoc-tab ${tab==='shift'?'is-active':''}`} 
          onClick={()=>setTab('shift')}
        >
          シフト入力
        </button>
        <button 
          className={`shiftdoc-tab ${tab==='duration'?'is-active':''}`} 
          onClick={()=>setTab('duration')}
        >
          時間枠設定
        </button>
      </div>

      {/* シフト入力タブ */}
      {tab === 'shift' && (
        <div className="shiftdoc-shift-pane">
          {/* 週ナビゲーション */}
          <div className="shiftdoc-monthbar">
            <button onClick={goPrevWeek}>‹</button>
            <div className="shiftdoc-monthlabel">
              {currentWeekStart && weekData ? 
                `${weekData.week_start} 〜 ${weekData.week_end}` : 
                '読込中…'}
            </div>
            <button onClick={goNextWeek} disabled={!canGoNextWeek} aria-label="次の週">›</button>
          </div>

          {/* アクションボタン */}
          <div className="shiftdoc-actions">
            <button 
              className="shiftdoc-btn-create" 
              onClick={openCreateModal}
            >
              シフトを入力する
            </button>
            <button 
              className={`shiftdoc-btn-delete ${selectedSlotIds.length > 0 ? 'is-active' : ''}`}
              onClick={deleteSelected}
              disabled={selectedSlotIds.length === 0}
            >
              削除する
            </button>
          </div>

          {/* 週次テーブル */}
          {loading ? (
            <div className="shiftdoc-loading">読込中…</div>
          ) : weekData ? (
            <div className="shiftdoc-week-table-wrapper">
              <table className="shiftdoc-week-table">
                <colgroup>
                  <col style={{ width: '56px' }} />
                  <col span={7} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="time-header">時間</th>
                    {weekData.days.map((day, idx) => (
                      <th key={idx}>
                        <span className="date-bold">{day.date.slice(8,10)}</span><br/>
                        <span className="dow-small">（{day.day_of_week}）</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {weekData.days[0].slots.map((_, slotIdx) => (
                    <tr key={slotIdx}>
                      <td className="time-cell">{weekData.days[0].slots[slotIdx].time}</td>
                      {weekData.days.map((day, dayIdx) => {
                        const slot = day.slots[slotIdx];
                        const selected = isSlotSelected(slot);
                        const canSelect = slot.state === 'full' || slot.state === 'half';
                        
                        return (
                          <td 
                            key={dayIdx}
                            className={`slot-cell state-${slot.state} ${selected ? 'is-selected' : ''} ${canSelect ? 'is-clickable' : ''}`}
                            onClick={() => canSelect && toggleSlot(dayIdx, slotIdx)}
                          >
                            {getSlotDisplay(slot.state)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="shiftdoc-empty">データがありません</div>
          )}
        </div>
      )}

      {/* 時間枠設定タブ */}
      {tab === 'duration' && (
        <div className="duradoc-pane">
          <div className="duradoc-section">
            <div className="duradoc-title">デフォルト時間（5分単位）</div>
            <div className="duradoc-row">
              <label>初診（10分以上）</label>
              <input
                type="number"
                inputMode="numeric"
                step={5} min={10} max={240}
                value={defDur.first_visit}
                onChange={e => onChangeDefault('first_visit', e.target.value)}
                onBlur={e => onBlurDefault('first_visit', e.target.value)}
              />
            </div>
            <div className="duradoc-row">
              <label>再診（5分以上）</label>
              <input
                type="number"
                inputMode="numeric"
                step={5} min={5} max={240}
                value={defDur.follow_up}
                onChange={e => onChangeDefault('follow_up', e.target.value)}
                onBlur={e => onBlurDefault('follow_up', e.target.value)}
              />
            </div>
          </div>

          <div className="duradoc-section">
            <div className="duradoc-title">科目ごとの時間（5分単位）</div>
            <div className="duradoc-table">
              <div className="duradoc-thead">
                <div>科目名</div>
                <div>初診（10分〜）</div>
                <div>再診（5分〜）</div>
              </div>
              <div className="duradoc-tbody">
                {specRows.map((r, i) => (
                  <div key={r.specialty_id} className="duradoc-rowline">
                    <div className="duradoc-spname">{r.specialty_name}</div>
                    <div>
                      <input
                        type="number"
                        inputMode="numeric"
                        step={5} min={10} max={240}
                        value={r.first_visit}
                        onChange={e => onChangeRow(i, 'first_visit', e.target.value)}
                        onBlur={e => onBlurRow(i, 'first_visit', e.target.value)}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        inputMode="numeric"
                        step={5} min={5} max={240}
                        value={r.follow_up}
                        onChange={e => onChangeRow(i, 'follow_up', e.target.value)}
                        onBlur={e => onBlurRow(i, 'follow_up', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 疾患別設定セクション */}
          <div className="duradoc-section">
            <div className="duradoc-title">疾患ごとの時間（5分単位）</div>
            <p className="duradoc-description">
              特定の疾患に対して個別の診察時間を設定できます。<br/>
              空欄の場合は科目設定またはデフォルト時間が適用されます。
            </p>
            <button 
              className="duradoc-add-disease-btn"
              onClick={() => setShowDiseaseModal(true)}
            >
              ＋ 疾患を追加
            </button>
            {diseaseRows.length === 0 ? (
              <div className="duradoc-empty">
                疾患別の設定はありません
              </div>
            ) : (
              <div className="duradoc-disease-list">
                {diseaseRows.map((r, i) => (
                  <div key={r.disease_id} className="duradoc-disease-item">
                    <div className="duradoc-disease-header">
                      <div className="duradoc-disease-name">
                        {r.disease_name}
                        <span className="duradoc-disease-specialty">{r.specialty_name}</span>
                      </div>
                      <button
                        className="duradoc-disease-remove"
                        onClick={() => removeDisease(i)}
                      >
                        削除
                      </button>
                    </div>
                    <div className="duradoc-disease-inputs">
                      <div className="duradoc-disease-input-group">
                        <label>初診</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          step={5} min={10} max={240}
                          placeholder="-"
                          value={r.first_visit}
                          onChange={e => onChangeDiseaseRow(i, 'first_visit', e.target.value)}
                          onBlur={e => onBlurDiseaseRow(i, 'first_visit', e.target.value)}
                        />
                        <span>分</span>
                      </div>
                      <div className="duradoc-disease-input-group">
                        <label>再診</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          step={5} min={5} max={240}
                          placeholder="-"
                          value={r.follow_up}
                          onChange={e => onChangeDiseaseRow(i, 'follow_up', e.target.value)}
                          onBlur={e => onBlurDiseaseRow(i, 'follow_up', e.target.value)}
                        />
                        <span>分</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="duradoc-actions" style={{ position:'fixed', left:0, right:0, bottom:8, padding:'12px 16px', background:'#fff', boxShadow:'0 -2px 10px rgba(0,0,0,0.06)', zIndex: 20 }}>
            <button className="shiftdoc-primary" onClick={saveAllDurations}>
              保存
            </button>
          </div>
        </div>
      )}

      {/* 疾患追加モーダル */}
      {showDiseaseModal && (
        <div className="shiftdoc-modal-overlay" onClick={() => setShowDiseaseModal(false)}>
          <div className="shiftdoc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shiftdoc-modal-head">
              <span>疾患を追加</span>
              <button onClick={() => setShowDiseaseModal(false)}>×</button>
            </div>
            <div style={{ padding: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem' }}>疾患を選択</label>
              <select
                value={selectedDiseaseId}
                onChange={(e) => setSelectedDiseaseId(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
              >
                <option value="">選択してください</option>
                {availableDiseases
                  .filter(d => !diseaseRows.some(r => r.disease_id === d.id))
                  .map(d => (
                    <option key={d.id} value={d.id}>
                      {d.disease_name}（{d.specialty_name}）
                    </option>
                  ))}
              </select>
              <button
                className="shiftdoc-primary"
                onClick={addDisease}
                disabled={!selectedDiseaseId}
                style={{ marginTop: '16px', width: '100%' }}
              >
                追加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* シフト作成モーダル */}
      {isCreateModalOpen && (
        <div className="shiftdoc-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
          <div className="shiftdoc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shiftdoc-modal-head">
              <span>シフトを作成</span>
              <button onClick={() => setIsCreateModalOpen(false)}>×</button>
            </div>

            <div className="shiftdoc-timeform">
              <div className="shiftdoc-field">
                <label className="shiftdoc-tlabel">日付</label>
                <input 
                  type="date" 
                  value={selectedDate || ''} 
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="shiftdoc-date-input"
                  min={weekData?.entry_min_date}
                  max={weekData?.entry_max_date}
                />
              </div>

              <div className="shiftdoc-timeblock">
                <div className="shiftdoc-tlabel">開始時間</div>
                <div className="shiftdoc-row">
                  <select value={startH} onChange={e=>setStartH(Number(e.target.value))}>
                    {HOUR_OPTS.map(h => <option key={h} value={h}>{pad2(h)}</option>)}
                  </select>
                  <span>時</span>
                  <select value={startM} onChange={e=>setStartM(Number(e.target.value))}>
                    {MIN_OPTS.map(m => <option key={m} value={m}>{pad2(m)}</option>)}
                  </select>
                  <span>分</span>
                </div>
              </div>

              <div className="shiftdoc-timeblock">
                <div className="shiftdoc-tlabel">終了時間</div>
                <div className="shiftdoc-row">
                  <select value={endH} onChange={e => {
                    const newH = Number(e.target.value);
                    setEndH(newH);
                    // 24時を選択した場合、分を00にリセット
                    if (newH === 24) setEndM(0);
                  }}>
                    {HOUR_OPTS_END.map(h => <option key={h} value={h}>{pad2(h)}</option>)}
                  </select>
                  <span>時</span>
                  <select 
                    value={endM} 
                    onChange={e=>setEndM(Number(e.target.value))}
                    disabled={endH === 24} // 24時の場合は分を選択不可
                  >
                    {/* 24時の場合は00のみ、それ以外は通常の選択肢 */}
                    {endH === 24 
                      ? <option value={0}>{pad2(0)}</option>
                      : MIN_OPTS.map(m => <option key={m} value={m}>{pad2(m)}</option>)
                    }
                  </select>
                  <span>分</span>
                </div>
              </div>

              <div className="shiftdoc-duration-preview">
                勤務時間 <strong>{durationText}</strong>
              </div>

              <div style={{ 
                fontSize: '0.75rem', 
                color: '#dc2626', 
                marginTop: '0.75rem', 
                marginBottom: '0.75rem',
                padding: '0.5rem',
                backgroundColor: '#fef2f2',
                borderRadius: '4px',
                lineHeight: '1.5'
              }}>
                ※ 今から2時間以降先のシフトのみ入力できます。<br/>
                ※ 入力できるのは本日から2か月先までです。<br/>
                ※ すでに予約が入っている場合、シフトのキャンセルはできません。
              </div>

              <button
                className="shiftdoc-primary"
                onClick={createShift}
                disabled={totalMinutes <= 0 || !selectedDate}
              >
                シフトを保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AlertModal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        iconType={alertModal.iconType}
        onClose={closeAlert}
        onConfirm={alertModal.onConfirm}
      />
      </div>
    </div>
  );
};

export default ShiftCalenderContainer;
